"use client";

import { useEffect, useState } from "react";
import { Star, RefreshCw, MessageSquare, ThumbsUp, ThumbsDown, Send, ExternalLink, AlertCircle } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

// ── Helpers ───────────────────────────────────────────────────

function StarRating({ rating }: { rating: number }) {
  return (
    <div style={{ display: "flex", gap: "2px" }}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={13} fill={i <= rating ? "#f59e0b" : "transparent"} color={i <= rating ? "#f59e0b" : "#d1d5db"} />
      ))}
    </div>
  );
}

function RatingBadge({ rating }: { rating: number }) {
  const color = rating >= 4 ? "#22c55e" : rating >= 3 ? "#f59e0b" : "#ef4444";
  return (
    <span style={{
      fontSize: "13px", fontWeight: 700, padding: "3px 10px",
      borderRadius: "20px", background: color + "18", color,
    }}>
      ⭐ {rating.toFixed(1)}
    </span>
  );
}

// ── Componente reseña ─────────────────────────────────────────

function ReviewCard({ review, onReply }: { review: any; onReply: (r: any) => void }) {
  const date = review.createTime
    ? new Date(review.createTime).toLocaleDateString("es-ES", { day: "numeric", month: "short" })
    : "—";
  const ratingMap: Record<string, number> = { FIVE: 5, FOUR: 4, THREE: 3, TWO: 2, ONE: 1 };
  const stars = ratingMap[review.starRating] ?? review.rating ?? 0;
  const hasReply = review.reviewReply?.comment || review.respuesta;
  const sentimentColor = stars >= 4 ? "#22c55e" : stars >= 3 ? "#f59e0b" : "#ef4444";
  const author = review.reviewer?.displayName || review.autor || "Usuario";

  return (
    <div onClick={() => onReply(review)} style={{
      background: "var(--bg-card)", borderRadius: "var(--radius-sm)",
      border: `1px solid ${hasReply ? "var(--border)" : sentimentColor + "30"}`,
      padding: "12px 16px", cursor: "pointer",
      transition: "all 150ms ease",
      display: "flex", alignItems: "flex-start", gap: "12px",
    }}
      onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-muted)")}
      onMouseLeave={e => (e.currentTarget.style.background = "var(--bg-card)")}
    >
      <div style={{
        width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
        background: sentimentColor + "20",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "13px", fontWeight: 700, color: sentimentColor,
      }}>
        {author[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3px" }}>
          <span style={{ fontWeight: 600, fontSize: "13px" }}>{author}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <StarRating rating={stars} />
            <span style={{ fontSize: "11px", color: "var(--text-light)" }}>{date}</span>
          </div>
        </div>
        {(review.comment || review.texto) && (
          <p style={{ fontSize: "12px", color: "var(--text-muted)", lineHeight: 1.4, margin: 0,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any }}>
            {review.comment || review.texto}
          </p>
        )}
        {hasReply
          ? <span style={{ fontSize: "11px", color: "var(--accent)", marginTop: "3px", display: "block" }}>✅ Respondida</span>
          : <span style={{ fontSize: "11px", color: sentimentColor, marginTop: "3px", display: "block" }}>💬 Sin responder</span>
        }
      </div>
    </div>
  );
}

// ── Modal respuesta ───────────────────────────────────────────

function ReplyModal({ review, onClose, onSent }: { review: any; onClose: () => void; onSent: () => void }) {
  const ratingMap: Record<string, number> = { FIVE: 5, FOUR: 4, THREE: 3, TWO: 2, ONE: 1 };
  const stars    = ratingMap[review.starRating] ?? review.rating ?? 0;
  const hasReply = review.reviewReply?.comment;
  const reviewId = review.reviewId || review.id;

  const [draft, setDraft]             = useState<any>(null);
  const [replyText, setReplyText]     = useState("");
  const [editing, setEditing]         = useState(false);
  const [sending, setSending]         = useState(false);
  const [status, setStatus]           = useState<'idle'|'confirmed'|'failed'>('idle');
  const [publishedAt, setPublishedAt] = useState<string|null>(null);
  const [loading, setLoading]         = useState(false);
  const [instructions, setInstructions] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (hasReply) return;
    setLoading(true);
    fetch(`${API}/inbox`)
      .then(r => r.json())
      .then((items: any[]) => {
        const found = items.find(i => {
          try { const d = JSON.parse(i.content); return d.type === 'review_draft' && d.reviewId === reviewId; } catch { return false; }
        });
        if (found) {
          const d = JSON.parse(found.content);
          setDraft({ ...d, inboxId: found.id });
          setReplyText(d.reply || '');
          setLoading(false);
        } else {
          fetch(`${API}/marketing/reviews/generate`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ author: review.reviewer?.displayName || 'Cliente', stars: review.starRating || stars, comment: review.comment || '' }),
          }).then(r => r.json()).then(d => { if (d.reply) setReplyText(d.reply); }).finally(() => setLoading(false));
        }
      }).catch(() => setLoading(false));
  }, []);

  const publish = async (text: string) => {
    setSending(true);
    try {
      const res  = await fetch(`${API}/marketing/reviews/${reviewId}/reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: text }),
      });
      const data = await res.json();
      if (draft?.inboxId) {
        await fetch(`${API}/marketing/reviews/draft/${draft.inboxId}/approve`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reply: text }),
        }).catch(() => {});
      }
      setStatus(data.published ? 'confirmed' : 'failed');
      setPublishedAt(data.publishedAt || null);
      setTimeout(() => { onSent(); onClose(); }, 2500);
    } catch { setStatus('failed'); }
    setSending(false);
  };

  const reject = async () => {
    if (draft?.inboxId) await fetch(`${API}/marketing/reviews/draft/${draft.inboxId}/reject`, { method: 'POST' }).catch(() => {});
    onClose();
  };

  const regenerate = () => {
    setLoading(true);
    setShowInstructions(false);
    fetch(`${API}/marketing/reviews/generate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        author: review.reviewer?.displayName || 'Cliente',
        stars: review.starRating || stars,
        comment: review.comment || '',
        instructions: instructions.trim() || undefined,
      }),
    }).then(r => r.json()).then(d => { if (d.reply) { setReplyText(d.reply); setInstructions(''); } }).finally(() => setLoading(false));
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,35,0.55)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: "var(--bg-card)", borderRadius: "20px", padding: "28px", width: "540px", maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", animation: "fade-in 0.2s ease" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "4px" }}>
            {hasReply ? '✅ Respuesta publicada' : draft ? '📝 Borrador de Vera — pendiente de aprobar' : '📣 Respuesta de Vera'}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
            {review.reviewer?.displayName || review.autor} · {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
          </div>
        </div>

        {/* Reseña original */}
        {(review.comment || review.texto) && (
          <div style={{ fontSize: "12px", color: "var(--text-light)", padding: "10px 14px", background: "var(--bg-muted)", borderRadius: "8px", lineHeight: 1.5, marginBottom: "16px", borderLeft: "3px solid var(--border)" }}>
            "{(review.comment || review.texto || '').slice(0, 300)}{(review.comment || '').length > 300 ? '...' : ''}"
          </div>
        )}

        {/* Ya respondida */}
        {hasReply && (
          <div style={{ background: "#22c55e10", border: "1px solid #22c55e30", borderRadius: "12px", padding: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#22c55e", marginBottom: "8px" }}>Respuesta de Varadero</div>
            <p style={{ fontSize: "13px", lineHeight: 1.6, margin: 0 }}>{hasReply}</p>
          </div>
        )}

        {/* Sin respuesta */}
        {!hasReply && (
          <>
            {loading ? (
              <div style={{ textAlign: "center", padding: "28px", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                <RefreshCw size={14} className="animate-spin" /> Vera está preparando la respuesta...
              </div>
            ) : status === 'idle' ? (
              <>
                {draft && (
                  <div style={{ fontSize: "11px", color: "var(--accent)", marginBottom: "8px", display: "flex", alignItems: "center", gap: "5px" }}>
                    📝 Borrador generado automáticamente por Vera
                  </div>
                )}
                {editing ? (
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={4} autoFocus
                    style={{ width: "100%", padding: "12px 14px", background: "var(--bg)", border: "1px solid var(--accent)", borderRadius: "10px", fontSize: "13px", color: "var(--text)", resize: "vertical", outline: "none", fontFamily: "inherit", lineHeight: 1.6 }} />
                ) : (
                  <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "10px", padding: "12px 14px", fontSize: "13px", lineHeight: 1.6, minHeight: "70px", color: replyText ? "var(--text)" : "var(--text-light)" }}>
                    {replyText || "Generando..."}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
                  <button onClick={() => setEditing(!editing)} style={{ fontSize: "12px", color: "var(--text-light)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                    <MessageSquare size={11} /> {editing ? 'Cerrar edición' : 'Editar'}
                  </button>
                  <button onClick={() => setShowInstructions(!showInstructions)} style={{ fontSize: "12px", color: "var(--text-light)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                    <RefreshCw size={11} /> Regenerar con indicaciones
                  </button>
                </div>

                {/* Campo de indicaciones para Vera */}
                {showInstructions && (
                  <div style={{ marginTop: "10px", background: "var(--bg-muted)", borderRadius: "10px", padding: "12px 14px", border: "1px solid var(--accent)30" }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--accent)", marginBottom: "6px" }}>Indicaciones para Vera</div>
                    <textarea
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                      placeholder="Ej: Hazla más corta. Meníona que lamentamos el inconveniente. Sé más formal..."
                      rows={2}
                      autoFocus
                      style={{ width: "100%", padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px", fontSize: "12px", color: "var(--text)", resize: "none", outline: "none", fontFamily: "inherit", lineHeight: 1.5 }}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) regenerate(); }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "8px" }}>
                      <button onClick={() => setShowInstructions(false)} style={{ fontSize: "11px", padding: "5px 10px", background: "none", border: "1px solid var(--border)", borderRadius: "6px", cursor: "pointer", color: "var(--text-muted)" }}>Cancelar</button>
                      <button onClick={regenerate} disabled={loading} style={{ fontSize: "11px", padding: "5px 12px", background: "var(--bg-dark)", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                        {loading ? <RefreshCw size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                        Regenerar
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "16px" }}>
                  <button onClick={reject} style={{ padding: "9px 16px", background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: "9px", fontSize: "12px", cursor: "pointer", color: "var(--text-muted)" }}>Descartar</button>
                  <button onClick={onClose} style={{ padding: "9px 16px", background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: "9px", fontSize: "12px", cursor: "pointer" }}>Cerrar</button>
                  <button onClick={() => publish(replyText)} disabled={sending || !replyText.trim()} style={{ padding: "9px 18px", background: "var(--bg-dark)", color: "white", border: "none", borderRadius: "9px", fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", opacity: !replyText.trim() ? 0.5 : 1 }}>
                    {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                    {sending ? 'Publicando...' : 'Aprobar y publicar'}
                  </button>
                </div>
              </>
            ) : status === 'confirmed' ? (
              <div style={{ textAlign: "center", padding: "24px", background: "#22c55e15", borderRadius: "12px", border: "1px solid #22c55e30" }}>
                <div style={{ fontSize: "26px", marginBottom: "8px" }}>📣</div>
                <div style={{ fontWeight: 600, color: "#22c55e", fontSize: "15px" }}>Vera ha publicado la respuesta en Google</div>
                {publishedAt && <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "6px" }}>{new Date(publishedAt).toLocaleString("es-ES", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}</div>}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "16px", background: "#f59e0b15", borderRadius: "12px" }}>
                <div style={{ color: "#f59e0b", fontWeight: 600 }}>⚠️ Sin confirmación de Google — verifica en Maps</div>
              </div>
            )}
          </>
        )}

        {hasReply && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
            <button onClick={onClose} style={{ padding: "9px 18px", background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: "9px", fontSize: "12px", cursor: "pointer" }}>Cerrar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────

const MOCK_REVIEWS = [
  { id: "1", reviewer: { displayName: "María García" }, starRating: "FIVE", comment: "Increíble experiencia. El arroz a banda es el mejor que he probado en años. El servicio, impecable. Volveremos sin duda.", createTime: "2026-04-10T12:00:00Z" },
  { id: "2", reviewer: { displayName: "Joan Puig" }, starRating: "FOUR", comment: "Muy buen restaurante, pescado fresco y bien cocinado. La terraza es una pasada. Bajé una estrella por la espera.", createTime: "2026-04-08T19:30:00Z" },
  { id: "3", reviewer: { displayName: "Sophie Martin" }, starRating: "FIVE", comment: "Best seafood restaurant in the area! The paella was amazing, and the staff was super friendly.", createTime: "2026-04-07T14:00:00Z", reviewReply: { comment: "Merci beaucoup Sophie! We're so happy you enjoyed your visit. See you soon!" } },
  { id: "4", reviewer: { displayName: "Carlos Ruiz" }, starRating: "TWO", comment: "Tardaron demasiado en atendernos y el precio no justifica la calidad de ese día. No fue nuestra mejor visita.", createTime: "2026-04-05T21:00:00Z" },
  { id: "5", reviewer: { displayName: "Ana Martínez" }, starRating: "FIVE", comment: "La fideuà de llamàntol... sin palabras. Y las vistas del Delta desde la terraza son preciosas. 10/10", createTime: "2026-04-03T13:30:00Z" },
  { id: "6", reviewer: { displayName: "Pierre Dubois" }, starRating: "FOUR", comment: "Excellent restaurant de fruits de mer. La vue est magnifique et le poisson très frais.", createTime: "2026-04-01T20:00:00Z" },
];

export default function ReputacionPage() {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "positive" | "negative">("all");
  const [googleConnected, setGoogleConnected] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const loadReviews = () => {
    Promise.all([
      fetch(`${API}/marketing/reviews/from-sheets`).then(r => r.json()).catch(() => ({ reviews: [] })),
      fetch(`${API}/marketing/reviews/pending-count`).then(r => r.json()).catch(() => ({ count: 0 })),
    ]).then(([d, pending]) => {
      if (d.reviews?.length) {
        const sorted = [...d.reviews].sort((a, b) => new Date(b.createTime || 0).getTime() - new Date(a.createTime || 0).getTime());
        setReviews(sorted);
        setGoogleConnected(true);
      } else {
        setReviews(MOCK_REVIEWS);
        setGoogleConnected(false);
      }
      setPendingCount(pending.count || 0);
      setLoading(false);
    });
  };

  useEffect(() => { loadReviews(); }, []);

  const ratingMap: Record<string, number> = { FIVE: 5, FOUR: 4, THREE: 3, TWO: 2, ONE: 1 };

  const SINCE = new Date('2026-03-01');
  const filteredReviews = reviews.filter(r => {
    const stars = ratingMap[r.starRating] ?? r.rating ?? 0;
    const hasReply = r.reviewReply?.comment || r.respuesta;
    const date = r.createTime ? new Date(r.createTime) : null;
    if (filter === "pending")  return !hasReply && (!date || date >= SINCE);
    if (filter === "positive") return stars >= 4 && (!date || date >= SINCE);
    if (filter === "negative") return stars <= 2 && (!date || date >= SINCE);
    // 'all' — mostrar desde marzo 2026
    return !date || date >= SINCE;
  });

  const SINCE_OWNERSHIP = new Date('2026-03-01');
  const reviewsSince = reviews.filter(r => {
    const date = r.createTime ? new Date(r.createTime) : null;
    return date && date >= SINCE_OWNERSHIP;
  });

  const avgRating = reviews.length > 0
    ? reviews.reduce((s, r) => s + (ratingMap[r.starRating] ?? r.rating ?? 0), 0) / reviews.length
    : 0;
  const avgRatingSince = reviewsSince.length > 0
    ? reviewsSince.reduce((s, r) => s + (ratingMap[r.starRating] ?? r.rating ?? 0), 0) / reviewsSince.length
    : 0;
  // pending ahora viene del backend (borradores en inbox), no del Sheet
  const positive = reviewsSince.filter(r => (ratingMap[r.starRating] ?? r.rating ?? 0) >= 4).length;

  return (
    <>
      <div className="animate-fade-in" style={{ width: "100%" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: 700 }}>Reputación</h1>
            <p style={{ color: "var(--text-muted)", marginTop: "6px" }}>Reseñas y presencia online de Varadero by Maury</p>
          </div>
          {!googleConnected && (
            <a href={`http://localhost:3200/auth/google`} target="_blank" rel="noreferrer" style={{
              display: "flex", alignItems: "center", gap: "7px",
              padding: "10px 18px", background: "var(--bg-dark)", color: "white",
              borderRadius: "10px", textDecoration: "none", fontSize: "13px", fontWeight: 500,
            }}>
              <ExternalLink size={14} /> Conectar Google My Business
            </a>
          )}
        </div>

        {/* Banner sin conectar */}
        {!googleConnected && (
          <div style={{ background: "#f59e0b18", border: "1px solid #f59e0b40", borderRadius: "12px", padding: "14px 20px", marginBottom: "24px", display: "flex", alignItems: "center", gap: "10px" }}>
            <AlertCircle size={16} color="#f59e0b" />
            <span style={{ fontSize: "13px", color: "#92400e" }}>
              Mostrando datos de ejemplo. Conecta Google My Business para ver reseñas reales y que Vera pueda responderlas automáticamente.
            </span>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "16px", marginBottom: "28px" }}>
          {[
            { label: "Valoración media", value: avgRating.toFixed(1) + " ⭐", color: "#f59e0b", sub: "Histórico total" },
            { label: "Desde mar. 2026",  value: avgRatingSince.toFixed(1) + " ⭐", color: "#f59e0b", sub: `${reviewsSince.length} reseñas con nosotros` },
            { label: "Total reseñas",    value: reviews.length,              color: "#3b82f6", sub: undefined },
            { label: "Sin responder",    value: pendingCount,                color: pendingCount > 0 ? "#ef4444" : "#22c55e", sub: undefined },
            { label: "Positivas (4-5★)", value: positive,                    color: "#22c55e", sub: "Desde mar. 2026" },
          ].map(s => (
            <div key={s.label} style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "20px 24px", boxShadow: "var(--shadow)" }}>
              <div style={{ fontSize: "28px", fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "4px" }}>{s.label}</div>
              {s.sub && <div style={{ fontSize: "11px", color: "var(--text-light)", marginTop: "2px" }}>{s.sub}</div>}
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          {([["all", "Todas"], ["pending", "Sin responder"], ["positive", "Positivas ★★★★★"], ["negative", "Negativas ★★"]] as const).map(([v, label]) => (
            <button key={v} onClick={() => setFilter(v)} style={{
              padding: "7px 16px", borderRadius: "20px", fontSize: "12px", fontWeight: filter === v ? 600 : 400,
              background: filter === v ? "var(--bg-dark)" : "var(--bg-muted)",
              color: filter === v ? "white" : "var(--text-muted)",
              border: `1px solid ${filter === v ? "var(--bg-dark)" : "var(--border)"}`,
              cursor: "pointer", transition: "all 150ms",
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* Layout 2 columnas: reseñas + redes */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "24px", alignItems: "start" }}>

          {/* Lista reseñas */}
          <div>
            {loading ? (
              <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                <RefreshCw size={16} className="animate-spin" /> Cargando...
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "460px", overflowY: "auto", paddingRight: "4px" }}>
                {filteredReviews.map(review => (
                  <ReviewCard key={review.id || review.reviewId} review={review} onReply={setSelectedReview} />
                ))}
                {filteredReviews.length === 0 && (
                  <div style={{ textAlign: "center", padding: "40px", color: "var(--text-light)" }}>
                    No hay reseñas en esta categoría
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Panel lateral: redes sociales */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <h2 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "4px" }}>Redes sociales</h2>

            {/* Instagram */}
            <a href="https://www.instagram.com/varaderobymaury" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", transition: "all 150ms" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-muted)")}
                onMouseLeave={e => (e.currentTarget.style.background = "var(--bg-card)")}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>📸</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "13px" }}>@varaderobymaury</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Ver perfil de Instagram</div>
                </div>
                <ExternalLink size={13} color="var(--text-light)" style={{ marginLeft: "auto" }} />
              </div>
            </a>

            {/* Facebook */}
            <a href="https://www.facebook.com/varaderobymaury" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", transition: "all 150ms" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-muted)")}
                onMouseLeave={e => (e.currentTarget.style.background = "var(--bg-card)")}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "#1877f2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>👥</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "13px" }}>Varadero by Maury</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Ver página de Facebook</div>
                </div>
                <ExternalLink size={13} color="var(--text-light)" style={{ marginLeft: "auto" }} />
              </div>
            </a>

            {/* Google Maps */}
            <a href="https://maps.google.com/?q=Varadero+by+Maury+Sant+Carles+de+la+Rapita" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", transition: "all 150ms" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--bg-muted)")}
                onMouseLeave={e => (e.currentTarget.style.background = "var(--bg-card)")}>
                <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "#34a853", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>📍</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "13px" }}>Google Maps</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{avgRating.toFixed(1)}★ · {reviews.length} reseñas</div>
                </div>
                <ExternalLink size={13} color="var(--text-light)" style={{ marginLeft: "auto" }} />
              </div>
            </a>

            {/* Stats rápidas */}
            <div style={{ background: "var(--bg-dark)", borderRadius: "var(--radius-sm)", padding: "16px", marginTop: "4px" }}>
              <div style={{ fontSize: "12px", color: "var(--text-on-dark-muted)", marginBottom: "10px" }}>Resumen Google</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {[
                  { label: "Media", value: avgRating.toFixed(1) + "★" },
                  { label: "Total", value: reviews.length },
                  { label: "Sin resp.", value: reviewsSince.filter((r: any) => !r.reviewReply?.comment).length },
                  { label: "Positivas", value: reviewsSince.filter((r: any) => (ratingMap[r.starRating] ?? 0) >= 4).length },
                ].map(s => (
                  <div key={s.label}>
                    <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-on-dark)" }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "var(--text-on-dark-muted)" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>

      {selectedReview && (
        <ReplyModal review={selectedReview} onClose={() => setSelectedReview(null)} onSent={loadReviews} />
      )}
    </>
  );
}
